import { autorun, makeAutoObservable, runInAction } from "mobx";
import { getDraftObjects } from "@/api/sdk.gen";
import { chatSession, SessionMessage } from "@/aawebsocket/chatSession";
import { chatState } from "./chatState";

export type DraftKey = { type: "artifact" | "tag"; id: number };

type DraftObjectsResponse = {
    conversations: { conversation_id: number; tag_ids: number[]; drafts: DraftKey[] }[];
};

export const scopeKey = (tagIds: number[]) => [...tagIds].sort((a, b) => a - b).join("-");
const objectKey = (key: DraftKey) => `${key.type}${key.id}`;

/**
 * Draft artifacts and tags of every conversation of the user, keyed by the conversation's tag scope.
 * Loaded from the backend; the open conversation is kept current from its chat objects.
 */
class DraftsState {
    scopes = new Map<string, Map<string, DraftKey>>();
    private loadGeneration = 0;

    constructor() {
        makeAutoObservable(this, { syncOpenConversation: false });
        chatSession.addListener(this.onMessage);
        autorun(this.syncOpenConversation);
    }

    onMessage = (message: SessionMessage) => {
        if (message.type === "run_status" && message.status !== "running") void this.load();
    };

    load = async () => {
        const generation = ++this.loadGeneration;
        const res = await getDraftObjects();
        const data = res.data as DraftObjectsResponse | undefined;
        if (!data || generation !== this.loadGeneration) return;
        runInAction(() => {
            this.scopes = new Map(
                data.conversations.map((c) => [scopeKey(c.tag_ids), new Map(c.drafts.map((d) => [objectKey(d), d]))]),
            );
            this.syncOpenConversation();
        });
    };

    /** Mirrors the draft flag of every loaded artifact and tag in the open conversation. */
    syncOpenConversation = () => {
        if (chatState.tagIds.length === 0) return;
        const tagIds = [...chatState.tagIds];
        const flags: [DraftKey, boolean][] = [];
        for (const obj of chatState.chatObjects.values()) {
            const isDraft = obj.type === "artifact" ? obj.artifact?.is_draft : obj.type === "tag" ? obj.tag?.is_draft : undefined;
            if (typeof isDraft === "boolean") flags.push([{ type: obj.type as DraftKey["type"], id: obj.id }, isDraft]);
        }
        runInAction(() => flags.forEach(([key, isDraft]) => this.set(tagIds, key, isDraft)));
    };

    set(tagIds: number[], key: DraftKey, isDraft: boolean) {
        const scope = scopeKey(tagIds);
        const drafts = this.scopes.get(scope);
        if (isDraft) {
            if (drafts?.has(objectKey(key))) return;
            if (drafts) drafts.set(objectKey(key), key);
            else this.scopes.set(scope, new Map([[objectKey(key), key]]));
        } else if (drafts?.delete(objectKey(key)) && drafts.size === 0) {
            this.scopes.delete(scope);
        }
    }

    /** Deleted objects stop being drafts everywhere. */
    remove(keys: { type: string; id: number }[]) {
        for (const key of keys) {
            if (key.type !== "artifact" && key.type !== "tag") continue;
            for (const [scope, drafts] of this.scopes) {
                if (drafts.delete(objectKey(key as DraftKey)) && drafts.size === 0) this.scopes.delete(scope);
            }
        }
    }

    countForScope(tagIds: number[]): number {
        return this.scopes.get(scopeKey(tagIds))?.size ?? 0;
    }

    /** Drafts across every conversation whose scope includes this tag. */
    countForTag(tagId: number): number {
        let count = 0;
        for (const [scope, drafts] of this.scopes) {
            if (scope.split("-").map(Number).includes(tagId)) count += drafts.size;
        }
        return count;
    }

    clear() {
        this.loadGeneration++;
        this.scopes.clear();
    }
}

export const draftsState = new DraftsState();
